import { useCallback, useMemo } from "react";
import type {
  EntitySwitcherItem,
  EntitySwitcherItemsResult,
  EntitySwitcherKind,
} from "./types";

/**
 * Picks the tab to land on after switching entity: keep the one the user is
 * already looking at, fall back to the entity's default, and fall back again
 * to whatever the target actually has.
 *
 * Tab keys are only ever compared within one entity type, so the repo's
 * inconsistent vocabularies (`access_policies` vs `access-policies` vs
 * `overview`) never need normalizing.
 */
export const resolveTargetTab = (
  currentTabKey: string | undefined,
  availableTabKeys: string[],
  defaultTabKey: string,
): string => {
  if (currentTabKey && availableTabKeys.includes(currentTabKey)) {
    return currentTabKey;
  }
  if (availableTabKeys.includes(defaultTabKey)) {
    return defaultTabKey;
  }
  return availableTabKeys[0] ?? defaultTabKey;
};

/**
 * Test ids are scoped by entity kind rather than defaulted.
 *
 * `Breadcrumb` supports more than one switcher crumb per page, so a fixed
 * `breadcrumb-switcher-trigger` would collide the moment a second crumb opts
 * in — the failure mode CLAUDE.md's "no default test id on a repeatable
 * component" rule exists to prevent.
 */
export const switcherTestId = (
  kind: EntitySwitcherKind,
  part: string,
): string => `breadcrumb-switcher-${part}-${kind}`;

/**
 * Shapes a descriptor's rows into the result the popover consumes.
 *
 * Every descriptor ends its `useItems` the same way — map the API rows through
 * its own `toItem` and pass the query flags along — so the shape lives here
 * rather than being re-assembled per entity type.
 */
export const useEntityItems = <T>(
  rows: T[],
  toItem: (row: T) => EntitySwitcherItem,
  {
    isLoading = false,
    isError = false,
    total,
  }: { isLoading?: boolean; isError?: boolean; total?: number } = {},
): EntitySwitcherItemsResult =>
  useMemo(
    () => ({ items: rows.map(toItem), isLoading, isError, total }),
    [rows, toItem, isLoading, isError, total],
  );

/**
 * For an entity whose tab set is the same for every sibling: the resolver
 * ignores the row and returns one stable list.
 */
export const useConstantTabKeys = (
  keys: string[],
): ((item: EntitySwitcherItem) => string[]) => useCallback(() => keys, [keys]);
